import React, { FC, useRef, useEffect, useState } from 'react';

import { CardSchema, HighlightSchema } from 'constants/graphqlTypes';
import { COLORS } from 'styles/constants';
import { TEST_CARD_IMMERSIVE, getCardSourceLabelId } from 'constants/aqa/card';

import handleVibrate from 'helpers/handleVibrate';
import getStyles from 'helpers/getStyles';
import getImages from 'helpers/getImages';
import Grain from 'components/UI/Grain';
import CardFooterMenu from 'components/UI/CardFooterMenu/CardFooterMenu';
import { useReactionMenu } from 'components/Reactions/helpers/hooks';
import ReactionsModal from 'components/Reactions/ReactionsModal';
import Content from '../Common/Content';
import Source from '../Common/Source';
import Highlight from '../Common/Highlight';
import Actions from '../Actions';

import {
  StyledWrap,
  StyledComment,
  StyledLink,
  StyledImage,
  StyledImageContainer,
  StyledContainer,
  StyledFooter,
} from './styled';

const Immersive: FC<{ card: CardSchema; highlight?: HighlightSchema | null; dataTestId?: string }> = ({
  card,
  highlight,
  dataTestId,
}) => {
  const { cardStyle } = card;
  const { bgColor } = getStyles(cardStyle);
  const { imageUrl } = getImages(cardStyle);

  const backgroundRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const { sourceUrl } = card.externalSources?.items[0] ?? {};

  const {
    setIsReactionsModalOpen,
    setIsQuickReactionOpen,
    setReactionCoordinates,
    isReactionsModalOpen,
    isQuickReactionOpen,
  } = useReactionMenu();

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const triggerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInTriggerZone = useRef(false);

  // Background image animation logic
  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      const { intersectionRatio, boundingClientRect } = entry;
      const viewportHeight = window.innerHeight;

      // Calculate trigger zone (center 80% of screen height)
      const triggerZoneTop = viewportHeight * 0.1; // 10% from top
      const triggerZoneBottom = viewportHeight * 0.9; // 90% from top

      // Check if card is in trigger zone and 70% visible
      const isInZone =
        boundingClientRect.top <= triggerZoneBottom &&
        boundingClientRect.bottom >= triggerZoneTop &&
        intersectionRatio >= 0.7;

      if (isInZone && !isInTriggerZone.current) {
        // Card entered trigger zone with 70% visibility
        isInTriggerZone.current = true;

        // Start 1-second timer before animation begins
        triggerTimeoutRef.current = setTimeout(() => {
          setIsAnimating(true);
        }, 1000);
      } else if (!isInZone && isInTriggerZone.current) {
        // Card left trigger zone
        isInTriggerZone.current = false;
        setIsAnimating(false);

        // Clear timeout if card leaves before 1 second
        if (triggerTimeoutRef.current) {
          clearTimeout(triggerTimeoutRef.current);
          triggerTimeoutRef.current = null;
        }
      }
    };

    // Intersection observer for trigger zone
    const imageObserver = new IntersectionObserver(handleIntersection, {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      rootMargin: '10% 0px -10% 0px', // Trigger zone: center 80% of screen
    });

    if (imageRef.current) {
      imageObserver.observe(imageRef.current);
    }

    return () => {
      imageObserver.disconnect();
      if (triggerTimeoutRef.current) {
        clearTimeout(triggerTimeoutRef.current);
      }
    };
  }, []);

  return (
    <StyledContainer>
      {imageUrl && (
        <StyledImageContainer ref={imageRef}>
          <StyledImage src={imageUrl} alt="image for card" isAnimating={isAnimating} />
        </StyledImageContainer>
      )}
      <StyledWrap
        ref={backgroundRef}
        bg={imageUrl ? COLORS.transparent : bgColor}
        isReverse={false}
        onClick={({ target }) => {
          if (target === backgroundRef.current) {
            window.open(sourceUrl ?? '#', '_blank', 'noopener,noreferrer');
            handleVibrate();
          }
        }}
      >
        <StyledComment>
          <Actions
            withoutPadding
            item={card}
            fontColor={COLORS.white[100]}
            showAddReaction
            isReactionButtonMenuShown={isQuickReactionOpen}
            setIsReactionButtonMenuShown={setIsQuickReactionOpen}
            setIsReactionsModalOpen={setIsReactionsModalOpen}
            setIsQuickReactionOpen={setIsQuickReactionOpen}
            setReactionCoordinates={setReactionCoordinates}
            onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
          />
        </StyledComment>
        <Grain />
        <StyledLink href={sourceUrl ?? '#'} onClick={handleVibrate} target="_blank" rel="noopener noreferrer">
          <Content dataTestId={dataTestId ?? TEST_CARD_IMMERSIVE} card={card} hugeTitle />
        </StyledLink>
        <StyledFooter color={COLORS.white[100]} hasImage={!!imageUrl}>
          <Source dataTestId={getCardSourceLabelId(dataTestId ?? TEST_CARD_IMMERSIVE)} card={card} />
          <CardFooterMenu card={card} color={COLORS.white[100]} />
        </StyledFooter>
        {highlight && <Highlight highlight={highlight} />}
      </StyledWrap>
      {isReactionsModalOpen && <ReactionsModal id={card.id} handleClose={() => setIsReactionsModalOpen(false)} />}
    </StyledContainer>
  );
};

export default Immersive;
